import { cloneElement } from 'react';
import { renderContentfulComponent } from '../../../../../components/contentful/content-type-renderers';
import { Table, Tabs, RichText, Heading } from '@lululemon/b2b-ecom-pattern-library';
import classNames from 'classnames';
import styles from './size-guide-primary-content.module.scss';
import { PrimaryContent } from '../types';
import { getTableSizeGrade, modifyContent } from '../utils';

type Props = {
  customStyles?: any;
  primaryContent: PrimaryContent;
  productDisplayName?: string;
  productSizeGrade?: string;
  sizeGuideCategory?: string;
  [key: string]: any;
};

export const SizeGuidePrimaryContent = ({
  customStyles = {},
  primaryContent,
  productDisplayName = '',
  productSizeGrade,
  sizeGuideCategory,
  ...extraProps
}: Props) => {
  if (primaryContent) {
    modifyContent(primaryContent, sizeGuideCategory);
  }

  if (!primaryContent) {
    return null;
  }

  return (
    <section
      className={classNames(styles.primaryContent, 'lll-font-weight-regular', customStyles?.primaryContent)}
      data-testid="size-guide__primary-content-section_test-id"
      {...extraProps}
    >
      {renderContentfulComponent({
        contentfulComponent: primaryContent,
        options: {
          renderContentType: {
            webTabpanel: ({
              data,
              children,
              contentfulComponent,
            }: {
              data: any;
              children: any;
              contentfulComponent: any;
            }) => {
              const { title, subtitle } = data;
              const getPanels = () => {
                return contentfulComponent.fields.components.map((component: any, index: number) => {
                  return {
                    label: component.fields.title,
                    content: children[index],
                  };
                });
              };
              const panels = getPanels();
              return <Tabs title={title} subtitle={subtitle} panels={panels} />;
            },
            webTabpanelPanel: ({ children }: { children: any }) => {
              return <>{children}</>;
            },

            webTable: ({
              data: { columnHeading, rows },
              contentfulComponent,
            }: {
              data: {
                columnHeading: {
                  value: string;
                }[];
                rows: any[];
              };
              contentfulComponent: any;
            }) => {
              const contourFitType = 'Contour Fit';
              const contourFitTypeFr = 'Coupe contour';
              const tableName = contentfulComponent?.fields?.contentfulEntryName || '';
              const isContourFitLeggings =
                productDisplayName.includes(contourFitType) || productDisplayName.includes(contourFitTypeFr);
              const isContourFitTable = tableName.includes(contourFitType);
              const tableStyles = {
                tableCell: customStyles?.tableCell || styles.tableCell,
                tableColHeader: customStyles?.tableColHeader || styles.tableColHeader,
                tableContainer: customStyles?.tableContainer || styles.tableContainer,
                table: customStyles?.table || styles.table,
              };

              if (
                (isContourFitLeggings && isContourFitTable) ||
                productSizeGrade === getTableSizeGrade(tableName) ||
                !productSizeGrade
              ) {
                return <Table classes={tableStyles} columnHeading={columnHeading} rows={rows} />;
              }
              return null;
            },
            richTextBody: ({ data: { body }, options }: { data: { body: any }; options: any }) => {
              return (
                <RichText body={body} options={options} customEntryRenderer={options?.renderContentfulComponent} />
              );
            },
            heading: ({
              data,
            }: {
              data: {
                subtitle?: string;
                title?: string;
                webHeadingStyle?: string;
                webHeadingLevel?: keyof JSX.IntrinsicElements;
                showLineAccent?: boolean;
                headerId?: string;
              };
            }) => {
              const { subtitle, title, webHeadingStyle, webHeadingLevel, showLineAccent, headerId } = data;
              if (!webHeadingLevel) return null;
              const hdgClass = webHeadingStyle ? `lll-text-${webHeadingStyle}` : null;
              return (
                <Heading
                  className={classNames(styles.heading, hdgClass, customStyles?.heading)}
                  tag={webHeadingLevel}
                  subHeading={subtitle}
                  showLineAccent={showLineAccent}
                  id={headerId}
                >
                  {title}
                </Heading>
              );
            },
          },
          transformElement: ({ element, contentType }: { element: any; contentType: any }) => {
            if (contentType === 'link') {
              return cloneElement(element, {
                className: classNames(element.props.className, styles.link),
              });
            }

            if (contentType === 'richTextBody') {
              return cloneElement(element, {
                className: classNames(element.props.className, customStyles?.richTextBody || styles.richTextBody),
              });
            }

            if (contentType === 'heading') {
              return cloneElement(element, {
                className: classNames(element.props.className, 'lll-type-heading-small'),
                wrapperClassName: customStyles?.headingContainer,
              });
            }

            return element;
          },
        },
      })}
    </section>
  );
};
